import React, { useState, useEffect } from "react";
import { TServicePlanViaAddress, TCustomerPlan, TPlan } from "types/service-plan";
import PlanOptionModal from "views/service-plans/components/PlanOptionModal";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import TooltippedElement from "components/TooltippedElement";
import { useModalContext } from "components/modal";
import { useMutation, useQuery } from "react-query";
import axios from "axios";
import { RiFileCloudLine, RiFileForbidFill, RiMore2Fill } from "react-icons/ri";
import { HiChevronDoubleDown, HiChevronDoubleUp } from "react-icons/hi";

const RequestServicePlans = ({ addressId }: { addressId: string }) => {
  const { t } = useTranslation();
  const tBase = "views.request.container.RequestServicePlans";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { setModal } = useModalContext();

  const [showDescription, setShowDescription] = useState<Set<number>>(new Set());

  const toggleDescription = (planId: number) => {
    setShowDescription((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(planId)) {
        newSet.delete(planId);
      } else {
        newSet.add(planId);
      }
      return newSet;
    });
  };

  const { data: planData } = useQuery<TServicePlanViaAddress[]>(
    ["servicePlans", addressId],
    async () => {
      const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/service-plans/address/${addressId}`);
      return response.data;
    },
    {
      enabled: !!addressId,
      refetchOnWindowFocus: true,
    }
  );

  const getPDFcontract = useMutation(async (info: { servicePlanId: string; customerId: string }) => {
    return axios
      .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/service-plans/file`,
        { params: info }
      )
      .then((res) => {
        return res.data;
      });
  });
  
  const columns = [
    { header: tr("Plan Name"), width: "w-[19%]" },
    { header: tr("Plan Description"), width: "w-[38%]" },
    { header: "", width: "w-[3%]" },
    { header: tr("Approval Date"), width: "w-[10%] text-center" },
    { header: tr("Start Date"), width: "w-[10%] text-center" },
    { header: tr("Duration"), subHeader: tr("(Months)"), width: "w-[10%] text-center" },
    { header: tr("Contract"), width: "w-[10%] text-center "},
  ];

  // return (<></>)

  return (
    <div>
      <div className="flex border-b-2 border-gray-300">
        {columns.map((col) => (
          <div key={col.header} className={"p1 " + col.width}>
            {col.header}
            {col.subHeader && (<div className="text-xs text-gray-500">{col.subHeader}</div>)}
          </div>
        ))}
      </div>
      <div>
        {!planData
          ? (
            <div className="flex justify-center items-center h-32 text-gray-500">
              {tr("No service plans found for this address.")}
            </div>
          )
          : planData.map((plan, i) => {
            const isContract = plan?.sp.is_contract;

            return (
              <div
                key={plan.sptc.id}
                className={`
                  flex border-b border-gray-300 pt-3 pb-2 pl-2 text-gray-800
                  ${plan.sptc.archived ? "bg-red-100" : "bg-white"}
                `}
              >
                {/* Plan Name */}
                <div className={columns[0].width}>

                    <p>{plan.sp.name}</p>
                </div>
                {/* Plan Description */}
                <div className={columns[1].width}>
                  <div
                    className={`
                      transition-all duration-300 ease-in-out overflow-hidden
                      ${
                        showDescription.has(i)
                          ? "max-h-[300px] whitespace-pre-wrap text-gray-700 overflow-y-auto"
                          : "max-h-[22.5px] truncate"
                      }
                    `}
                  >
                    {plan?.sp.description}
                  </div>
                </div>
                {/* Toggle for description */}
                <div className={columns[2].width}>
                  <button onClick={() => toggleDescription(i)}>
                    {showDescription.has(i) ? (
                      <HiChevronDoubleUp size={20} className="text-primary hover:bg-slate-100 hover:border rounded-[5px]" />
                    ) : (
                      <HiChevronDoubleDown
                        size={20}
                        className="text-primary hover:bg-slate-100 hover:border rounded-[5px]"
                      />
                    )}
                  </button>
                </div>
                {/* Approval Date */}
                <div className={columns[3].width}>
                  {plan.sptc.date_signed ? (
                    <span>{format(new Date(plan.sptc.date_signed), "MMM dd, yyyy")}</span>
                  ) : (
                    <span className="text-[#ff500890]">{tr("Pending")}</span>
                  )}
                </div>
                {/* Start Date */}
                <div className={columns[4].width}>
                  {plan.sptc.start_date ? format(new Date(plan.sptc.start_date), "MMM dd, yyyy") : "--"}
                </div>
                {/* Duration */}
                <div className={columns[5].width}>{plan.sp.duration}</div>
                {/* Contract */}
                <div className={columns[6].width}>
                  <TooltippedElement
                    message={
                      isContract
                        ? tr("View approved contract")
                        : tr("Contract not required")
                    }
                    placement="bottom"
                    className="-mb-1"
                    element={
                      <button
                        className={`${
                          isContract ? "cursor-pointer" : "cursor-auto"
                        }`}
                        onClick={async () => {
                          if (!isContract) return;
                          getPDFcontract
                            .mutateAsync({
                              servicePlanId: plan.sp.id,
                              customerId: plan.sptc.business_customerid,
                            })
                            .then((res) => window.open(res));
                        }}
                      >
                        {isContract ? (
                          <RiFileCloudLine className="text-green-500" />
                        ) : (
                          <RiFileForbidFill className="text-red-500" />
                        )}
                      </button>
                    }
                  />
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default RequestServicePlans;